import { account, sendVerifiedOTP, storage, teams } from "@/lib/appwrite";
import { createContext, useEffect, useState, useContext, type ReactNode, useCallback, useMemo, useRef } from "react";
import { ID, OAuthProvider, type Models } from "appwrite";
import { createEmptyWorkerProfile } from "@/lib/collections/workerProfile-Users/addworkProfileMicrosoft";
import { saveOAuthAvatar } from "@/lib/storage/save-avatar-collaborators";
import { registerPushSubscription } from "@/hooks/pushweb-notifications/registerPush";

type UserRoles = {
  isRRHH: boolean;
  isAdmin: boolean;
  isServicioTecnico: boolean;
  isAsesorComercial: boolean;
  isSupervisor: boolean;
};

type User = Models.User<{}> | null;

export interface AuthContextType {
  user: User;
  userRoles: UserRoles;
  loading: boolean;
  isAuthenticated: boolean;
  loginUser: (userInfo: { email: string; password: string }) => Promise<void>;
  logoutUser: () => Promise<void>;
  registerUser: (userInfo: {
    email: string;
    password1: string;
    name: string;
  }) => Promise<void>;
  loginWithOAuth: (provider: OAuthProvider) => Promise<void>;
  checkUserStatus: () => Promise<void>;
  sendEmailOTP: (email: string) => Promise<{ userId: string }>;
  verifyEmailOTP: (userId: string, code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Constante para roles por defecto (evita recrear objeto en cada render)
const DEFAULT_ROLES: UserRoles = {
  isRRHH: false,
  isAdmin: false,
  isServicioTecnico: false,
  isAsesorComercial: false,
  isSupervisor: false,
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User>(null);
  const pushNotificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkUserStatusRef = useRef<(() => Promise<void>) | null>(null);

  // Inicializar desde localStorage
  const [userRoles, setUserRoles] = useState<UserRoles>(() => {
    const savedRoles = localStorage.getItem('userRoles');
    return savedRoles ? JSON.parse(savedRoles) : DEFAULT_ROLES;
  });

  const loginUser = useCallback(async (userInfo: { email: string; password: string }) => {
    setLoading(true);
    try {
      await account.createEmailPasswordSession(userInfo.email, userInfo.password);
      // Usar la referencia más reciente de checkUserStatus
      if (checkUserStatusRef.current) {
        await checkUserStatusRef.current();
      }
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const logoutUser = useCallback(async () => {
    setLoading(true);
    try {
      await account.deleteSession("current");
      setUser(null);
      setUserRoles(DEFAULT_ROLES);
      localStorage.removeItem('userRoles');
      localStorage.removeItem('hasValidSession');
      setIsAuthenticated(false);
    } catch (error) {
      // console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithOAuth = useCallback(async (provider: OAuthProvider) => {
    try {
      const successURL = import.meta.env.VITE_APPWRITE_OAUTH_SUCCESS;
      const failureURL = import.meta.env.VITE_APPWRITE_OAUTH_FAILURE;
      account.createOAuth2Session(provider, successURL, failureURL);
    } catch (error) {
      console.error("OAuth login error", error);
    }
  }, []);

  const sendEmailOTP = useCallback(async (email: string): Promise<{ userId: string }> => {
    try {
      const execution = await sendVerifiedOTP(email);
      const rawOutput = execution?.responseBody ?? "";
      const result = typeof rawOutput === "string" ? JSON.parse(rawOutput) : rawOutput;

      if (!result?.success) {
        throw new Error("email_not_found");
      }

      const token = await account.createEmailToken(ID.unique(), email);

      return { userId: token.userId };

    } catch (error: any) {
      throw error;
    }
  }, []);

  const verifyEmailOTP = useCallback(async (userId: string, code: string) => {
    setLoading(true);
    try {
      await account.createSession(userId, code);
      if (checkUserStatusRef.current) {
        await checkUserStatusRef.current();
      }
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const registerUser = useCallback(async (userInfo: {
    email: string;
    password1: string;
    name: string;
  }) => {
    setLoading(true);
    try {
      await account.create(
        ID.unique(),
        userInfo.email,
        userInfo.password1,
        userInfo.name
      );
      await loginUser({
        email: userInfo.email,
        password: userInfo.password1
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [loginUser]);

  const isMember = async (teamId: string, userId: string) => {
    try {
      const response = await teams.listMemberships(teamId);
      return response.memberships.some((member) => member.userId === userId);
    } catch {
      return false;
    }
  };

  const checkUserStatus = useCallback(async () => {
    setLoading(true);

    try {
      //Sesión y detalles
      const session = await account.getSession("current");
      const details = await account.get();
      const userId = details.$id;

      //Setea inmediatamente user e isAuthenticated
      setUser(details);
      setIsAuthenticated(!!session);

      localStorage.setItem('hasValidSession', 'true');


      //Comprueba si necesita subir avatar
      let avatarNeedsUpload = false;
      if (session.provider === "microsoft" && session.providerAccessToken) {
        const prefsAvatarId = details.prefs?.avatar;
        if (!prefsAvatarId) {
          avatarNeedsUpload = true;
        } else {
          const bucketId = import.meta.env.VITE_BUCKET_ID_AVATAR_COLLABORATOR!;
          const list = await storage.listFiles(bucketId);
          const found = list.files.some(f => f.$id === prefsAvatarId);
          if (!found) avatarNeedsUpload = true;
        }
      }

      // Si hace falta, lo sube sin promesas paralelas
      if (avatarNeedsUpload) {
        await saveOAuthAvatar(session.providerAccessToken!, userId);
        // refresca prefs y actualiza user de una sola vez
        const newPrefs = await account.getPrefs();
        const updatedDetails = { ...details, prefs: newPrefs as any };
        setUser(updatedDetails);
      }

      //Perfil y roles
      await createEmptyWorkerProfile(userId);
      const rolesToCheck = {
        isRRHH: import.meta.env.VITE_TEAM_RRHH_ID!,
        isAdmin: import.meta.env.VITE_TEAM_ADMIN_ID!,
        isServicioTecnico: import.meta.env.VITE_TEAM_SERVICIO_TECNICO_ID!,
        isAsesorComercial: import.meta.env.VITE_TEAM_ASESOR_COMERCIAL_ID!,
        isSupervisor: import.meta.env.VITE_TEAM_SUPERVISOR_ID!,
      };
      const updatedRoles: UserRoles = { isRRHH: false, isAdmin: false, isAsesorComercial: false, isServicioTecnico: false, isSupervisor: false, };
      await Promise.all(
        (Object.entries(rolesToCheck) as [keyof UserRoles, string][])
          .map(async ([key, teamId]) => {
            if (await isMember(teamId, userId)) updatedRoles[key] = true;
          })
      );
      setUserRoles(updatedRoles);
      localStorage.setItem("userRoles", JSON.stringify(updatedRoles));


      // Solo intentar si estamos en un entorno web (no server-side)
      if (typeof window !== 'undefined') {
        try {
          // Limpiar timeout anterior si existe
          if (pushNotificationTimeoutRef.current) {
            clearTimeout(pushNotificationTimeoutRef.current);
          }
          // Usar setTimeout para no bloquear el flujo principal de autenticación
          pushNotificationTimeoutRef.current = setTimeout(async () => {
            const pushRegistered = await registerPushSubscription(userId);
            if (pushRegistered) {
            } else {
            }
            pushNotificationTimeoutRef.current = null;
          }, 1000); // Esperar 1 segundo para que termine el flujo de login
        } catch (pushError) {
          // No mostramos error al usuario, solo lo logueamos
          // console.warn("Error configurando notificaciones push:", pushError);
        }
      }

    } catch (error) {
      // console.error("[checkUserStatus] error general:", error);
      setUser(null);
      setIsAuthenticated(false);
      setUserRoles(DEFAULT_ROLES);
      localStorage.setItem("userRoles", JSON.stringify(DEFAULT_ROLES));
      localStorage.removeItem('hasValidSession');
    } finally {
      setLoading(false);
    }
  }, []);

  // Mantener referencia actualizada de checkUserStatus
  useEffect(() => {
    checkUserStatusRef.current = checkUserStatus;
  }, [checkUserStatus]);

  // Verificar sesión al cargar
  useEffect(() => {
    const checkSession = async () => {
      try {
        // Verificar si hay un token de sesión válido
        const session = await account.getSession('current');
        if (session) {
          await checkUserStatus();
        } else {
          setIsAuthenticated(false);
          setLoading(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setLoading(false);
      }
    };

    checkSession();
  }, [checkUserStatus]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (pushNotificationTimeoutRef.current) {
        clearTimeout(pushNotificationTimeoutRef.current);
      }
    };
  }, []);

  // Memoizar el valor del contexto para evitar renders innecesarios
  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    userRoles,
    loading,
    isAuthenticated,
    loginUser,
    logoutUser,
    registerUser,
    loginWithOAuth,
    checkUserStatus,
    sendEmailOTP,
    verifyEmailOTP,
  }), [user, userRoles, loading, isAuthenticated, loginUser, logoutUser, registerUser, loginWithOAuth, checkUserStatus, sendEmailOTP, verifyEmailOTP]);
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error("useAuth debe usarse dentro de un AuthProvider.");
  return context;
};